import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { WatchlistEntry } from '@pleco/shared';
import { SearchCommand } from '../components/SearchCommand.js';
import { SceneCanvas, useInView } from '../components/landing/SceneCanvas.js';
import { FIELD_STATS, TrapField, type FieldStatus } from '../components/landing/TrapField.js';
import { HERO_PCT, HERO_PHASES, TRAPS, heroScene, heroZones, type RiskLevel } from '../components/landing/scenes.js';
import { BRAND, BrandMark } from '../brand.js';
import { api } from '../lib/api.js';
import { useDiscover } from '../lib/discover.js';
import { fmtPrice, fmtUsd } from '../lib/fmt.js';
import { Change, KindMark, TokenLogo } from '../components/TokenTicker.js';
import type { DiscoverToken } from '@pleco/shared';

/**
 * Home. One promise — "know who you're trading against" — told in four beats:
 *   1. Hero: paste a token; the live reveal shows a "spread out" crowd turning
 *      into the two crews that actually own it.
 *   2. The four traps beginners walk into, each played out on a mini map.
 *   3. The answer format: one risk word, plain reasons, receipts.
 *   4. The three rules for reading any map, then the search again.
 */
export function Landing({ signedOut = false }: { signedOut?: boolean }) {
  const [watch, setWatch] = useState<WatchlistEntry[]>([]);

  useEffect(() => {
    if (signedOut) return;
    api
      .watchlist()
      .then((r) => setWatch(r.entries.slice(0, 6)))
      .catch(() => undefined);
  }, [signedOut]);

  return (
    <div className="tm-landing lp">
      <Hero signedOut={signedOut} watch={watch} />
      <Browse />
      <Story />
      <Traps />
      <Answer />
      <MapKey />
      <Faq />
      <FinalCta signedOut={signedOut} />
      <footer className="lp-foot">
        <div className="lp-foot-brand">
          <BrandMark size={22} />
          <b>{BRAND.name}</b>
        </div>
        <span className="lp-foot-meta">
          © {new Date().getFullYear()} {BRAND.name} <i>/</i> Map data shown on this page is illustrative <i>/</i> Not financial advice
        </span>
        <nav>
          <a href="#traps">The traps</a>
          <a href="#answer">The verdict</a>
          <a href="#faq">FAQ</a>
          {signedOut ? <Link to="/signin">Sign in</Link> : <Link to="/watchlist">Watchlist</Link>}
          <SocialLinks />
        </nav>
      </footer>
    </div>
  );
}

/* ================================================================== hero */

const HERO_STEPS = [
  { n: '01', short: 'The holders', label: 'What the holder list shows', caption: '1,480 holders. Looks nicely spread out.', dur: HERO_PHASES[1] - HERO_PHASES[0] },
  { n: '02', short: 'The funding', label: 'Tracing shared funding', caption: 'Who funded each wallet, and who bought in the same block.', dur: HERO_PHASES[2] - HERO_PHASES[1] },
  { n: '03', short: 'The clusters', label: 'Who really controls the supply', caption: `Two clusters hold ${HERO_PCT.a + HERO_PCT.b}% of the supply.`, dur: heroScene.duration - HERO_PHASES[2] },
];

function Hero({ signedOut, watch }: { signedOut: boolean; watch: WatchlistEntry[] }) {
  const [st, setSt] = useState<FieldStatus>({ caught: 0, flagged: 0, hint: { text: 'Normal view: every cluster looks the same', alert: false } });
  const hint = st.hint;

  return (
    <section className="lp-hero lp-hero-field">
      <TrapField onStatus={setSt} />
      <div className="lp-hero-inner">
        <div className="lp-hero-copy">
          <p className="lp-eyebrow">
            <span className="lp-tag">
              <i /> Live beta
            </span>
            <span className="lp-eyebrow-t">Visual on-chain intelligence</span>
          </p>
          <h1>
            <span className="lp-h1-line">See who the real</span>
            <em className="lp-h1-line">bad actors are.</em>
          </h1>
          <p className="lp-lead">
            {BRAND.name} turns a token’s holder list into a live map of who actually controls the supply — clustering linked wallets, tracing
            shared funding, and flagging bundles and insider rings before they unload&nbsp;on&nbsp;you.
          </p>
          <SearchCommand variant="hero" locked={signedOut} />
          <div className="lp-ctas">
            <a className="lp-btn lp-btn-primary" href={BRAND.x} target="_blank" rel="noopener noreferrer">
              <IconX size={14} />
              Follow on X
              <span className="lp-arrow" aria-hidden="true">
                →
              </span>
            </a>
            <a className="lp-btn lp-btn-ghost" href={BRAND.telegram} target="_blank" rel="noopener noreferrer">
              <IconTelegram size={15} />
              Join the Telegram
            </a>
          </div>
          <p className="lp-detect">
            <span className="lp-detect-ico" aria-hidden="true" />
            <span className="lp-detect-k">proxima.tools</span> launches <b>detectable</b>
          </p>
          <ul className="lp-feats" aria-label="Capabilities">
            <li>
              <span>01</span>Wallet clustering
            </li>
            <li>
              <span>02</span>Funding-source tracing
            </li>
            <li>
              <span>03</span>Supply trap alerts
            </li>
          </ul>
          {watch.length > 0 && (
            <div className="tm-recent">
              <span>Your watchlist</span>
              {watch.map((w) => (
                <Link key={w.id} to={`/map/${w.chain}/${w.address}`} className="tm-chip">
                  {w.symbol ?? `${w.address.slice(0, 4)}…`}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <aside className="lp-hud" aria-label="Live map readout (simulated)">
        <div className="lp-hud-head">
          <span className="lp-hud-title">CLUSTER READOUT</span>
          <span className={`lp-hud-badge ${st.caught ? 'alert' : ''}`}>{st.caught ? 'TRAP FOUND' : 'NORMAL VIEW'}</span>
        </div>
        <dl>
          <div>
            <dt>Wallets mapped</dt>
            <dd>{FIELD_STATS.wallets}</dd>
          </div>
          <div>
            <dt>Clusters</dt>
            <dd>{FIELD_STATS.clusters}</dd>
          </div>
          <div>
            <dt>Bad actors caught</dt>
            <dd className={st.caught ? 'danger' : ''}>
              {st.caught} / {FIELD_STATS.traps}
            </dd>
          </div>
          <div>
            <dt>Flagged supply</dt>
            <dd className={st.caught ? 'danger' : ''}>{st.flagged.toFixed(1)}%</dd>
          </div>
        </dl>
        <p className={`lp-hud-hint ${hint.alert ? 'alert' : ''}`}>
          {hint.label && <b>{hint.label}</b>}
          {hint.label && ' · '}
          {hint.text}
        </p>
        <p className="lp-hud-how">
          <i aria-hidden="true">⟷</i> Drag the line: left of it is the {BRAND.name} view
        </p>
      </aside>
    </section>
  );
}

function Story() {
  const [ref, inView] = useInView<HTMLDivElement>({ margin: '0px' });
  const [headRef, headIn] = useInView<HTMLDivElement>({ once: true });
  const [phase, setPhase] = useState(0);
  const [jump, setJump] = useState<{ t: number; n: number }>();
  const step = HERO_STEPS[phase]!;

  return (
    <section className="lp-section lp-story" id="how">
      <div ref={headRef} className={`lp-story-copy lp-reveal ${headIn ? 'in' : ''}`}>
        <span className="lp-kicker">How it works</span>
        <h2>From holder list to trap map in seconds.</h2>
        <p>
          A holder list tells you how many wallets there are. It doesn’t tell you who owns them. {BRAND.name} follows the money behind every
          top holder and redraws the list as the groups that actually control the supply.
        </p>
        <ol className="lp-answer-points">
          <li>
            <span>1</span>
            <div>
              <b>Pull the holders.</b> Every top wallet, sized by the share of supply it holds right now.
            </div>
          </li>
          <li>
            <span>2</span>
            <div>
              <b>Trace the funding.</b> Who sent each wallet its first SOL, and who bought in the same block.
            </div>
          </li>
          <li>
            <span>3</span>
            <div>
              <b>Map the clusters.</b> Linked wallets collapse into clusters, and the ones hiding supply get flagged as traps.
            </div>
          </li>
        </ol>
      </div>

      <div className="lp-visual" ref={ref}>
          <div className="lp-stage-wrap">
            <div className="lp-stage">
              <SceneCanvas scene={heroScene} playing={inView} onPhase={setPhase} jump={jump} label="Example token: a spread-out holder list that turns out to be two linked clusters" />
              <div className="lp-caption" key={phase}>
                <small>{step.label}</small>
                <span>{step.caption}</span>
              </div>
              {heroZones.map((z, i) => (
                <span
                  key={i}
                  className={`lp-zone-chip ${phase === 2 ? 'on' : ''}`}
                  style={{ left: `${(z.cx / z.w) * 100}%`, top: `${((z.cy - z.R) / z.h) * 100}%`, ['--c' as string]: z.color }}
                >
                  Trap · {z.pct}%
                </span>
              ))}
              <span className="lp-example">Example</span>
            </div>
            <div className={`lp-readout ${phase === 2 ? 'on' : ''}`} aria-live="polite">
              <div className="lp-readout-head">
                <span>{BRAND.name} readout</span>
                <em>{phase === 2 ? 'Done' : phase === 1 ? 'Tracing' : 'Scanning'}</em>
              </div>
              <dl>
                <div>
                  <dt>Wallets mapped</dt>
                  <dd>1,480</dd>
                </div>
                <div>
                  <dt>Clusters</dt>
                  <dd>{phase === 2 ? '2' : phase === 1 ? <Dots /> : '—'}</dd>
                </div>
                <div>
                  <dt>Flagged supply</dt>
                  <dd className="hot">{phase === 2 ? `${HERO_PCT.a + HERO_PCT.b}%` : phase === 1 ? <Dots /> : '—'}</dd>
                </div>
              </dl>
              <div className="lp-readout-foot" key={phase}>
                {phase === 2 ? (
                  <>
                    <Badge level="high" />
                    <span>
                      <b>Supply trap detected.</b> 8 wallets, shared funder, bought in the launch block.
                    </span>
                  </>
                ) : (
                  <span className="lp-readout-q">{phase === 0 ? 'Who am I really trading against?' : 'Following the money behind every wallet…'}</span>
                )}
              </div>
            </div>
          </div>
          <ol className="lp-steps">
            {HERO_STEPS.map((s, i) => (
              <li key={s.n} className={i === phase ? 'on' : i < phase ? 'done' : ''}>
                <button onClick={() => setJump({ t: HERO_PHASES[i]! + 0.01, n: (jump?.n ?? 0) + 1 })}>
                  <span className="lp-step-bar">
                    <i key={`${i}-${phase}-${jump?.n ?? 0}`} style={{ animationDuration: `${s.dur}s`, animationPlayState: inView ? 'running' : 'paused' }} />
                  </span>
                  <span className="lp-step-n">{s.n}</span>
                  <span className="lp-step-l">{s.short}</span>
                </button>
              </li>
            ))}
          </ol>
        </div>
    </section>
  );
}

/* ================================================================ browse */

const BROWSE_TABS = [
  { id: 'popular', label: 'Popular' },
  { id: 'boosted', label: 'Boosted' },
  { id: 'new', label: 'New launches' },
] as const;

function Browse() {
  const { data, subscribe } = useDiscover();
  useEffect(() => subscribe(), [subscribe]);
  const [tab, setTab] = useState<(typeof BROWSE_TABS)[number]['id']>('popular');
  const [headRef, headIn] = useInView<HTMLDivElement>({ once: true });
  const list: DiscoverToken[] =
    tab === 'popular'
      ? (data?.popular ?? [])
      : tab === 'boosted'
        ? (data?.live ?? []).filter((t) => t.kind === 'boosted').sort((a, b) => (b.boost ?? 0) - (a.boost ?? 0))
        : (data?.live ?? []).filter((t) => t.kind === 'new' || t.isNew).sort((a, b) => (b.pairCreatedAt ?? 0) - (a.pairCreatedAt ?? 0));
  const shown = list.slice(0, 12);

  return (
    <section className="lp-section lp-browse" id="tokens">
      <div ref={headRef} className={`lp-browse-head lp-reveal ${headIn ? 'in' : ''}`}>
        <div>
          <span className="lp-kicker">Browse Solana</span>
          <h2>Pick a token. See who’s really holding it.</h2>
        </div>
        <div className="lp-tabs" role="tablist" aria-label="Token lists">
          {BROWSE_TABS.map((t) => (
            <button key={t.id} role="tab" aria-selected={tab === t.id} className={tab === t.id ? 'on' : ''} onClick={() => setTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="lp-tokens" role="tabpanel">
        {!data &&
          Array.from({ length: 8 }, (_, i) => (
            <div key={i} className="lp-tok-card skel" aria-hidden="true">
              <span className="tk-logo tk-logo-f" />
              <span className="skel-line" />
            </div>
          ))}
        {data && !shown.length && <p className="lp-tokens-empty">Nothing here right now. Check back in a minute.</p>}
        {shown.map((t, i) => (
          <Link key={t.address} to={`/map/sol/${t.address}`} className="lp-tok-card" style={{ animationDelay: `${i * 35}ms` }}>
            <TokenLogo t={t} size={38} />
            <span className="lp-tok-main">
              <span className="lp-tok-top">
                <b>{t.symbol}</b>
                <KindMark t={t} />
              </span>
              <span className="lp-tok-name">{t.name}</span>
            </span>
            <span className="lp-tok-side">
              <span className="lp-tok-price">{fmtPrice(t.priceUsd)}</span>
              <Change v={t.change24h} />
            </span>
            <span className="lp-tok-meta">
              <span>
                MC <b>{fmtUsd(t.marketCapUsd)}</b>
              </span>
              <span>
                Vol <b>{fmtUsd(t.volume24hUsd)}</b>
              </span>
              <span className="lp-tok-cta">Map it →</span>
            </span>
          </Link>
        ))}
      </div>
      <p className="lp-tokens-note">Live market data from DexScreener, refreshed every minute. Boosted means someone paid to promote it, not that it’s safe.</p>
    </section>
  );
}

/* ================================================================= traps */

function Traps() {
  const [active, setActive] = useState(0);
  const [phase, setPhase] = useState(0);
  const [auto, setAuto] = useState(true);
  const [ref, inView] = useInView<HTMLDivElement>();
  const [headRef, headIn] = useInView<HTMLDivElement>({ once: true });
  const trap = TRAPS[active]!;
  const dur = trap.scene.duration;

  // Auto-advance through the traps while the section is on screen, until the reader picks one.
  useEffect(() => {
    if (!auto || !inView) return;
    const id = setTimeout(() => setActive((a) => (a + 1) % TRAPS.length), dur * 1000 - 50);
    return () => clearTimeout(id);
  }, [active, auto, inView, dur]);

  const pick = (i: number) => {
    setAuto(false);
    setActive(i);
    setPhase(0);
  };

  return (
    <section className="lp-section" id="traps">
      <div ref={headRef} className={`lp-head lp-reveal ${headIn ? 'in' : ''}`}>
        <span className="lp-kicker">The traps</span>
        <h2>Four traps that unload on you. Every one shows up on the map.</h2>
        <p>
          Bundles, insider rings and farmed holders barely register on a chart. They hide in wallets that look independent until you map who
          funded them. Here’s what each one looks like.
        </p>
      </div>

      <div className="lp-traps" ref={ref}>
        <ol className="lp-trap-list" role="tablist" aria-label="Common traps">
          {TRAPS.map((t, i) => (
            <li key={t.id} className={i === active ? 'on' : ''}>
              <button role="tab" aria-selected={i === active} onClick={() => pick(i)}>
                {i === active && auto && (
                  <span className="lp-trap-progress">
                    <i key={`${active}`} style={{ animationDuration: `${dur}s`, animationPlayState: inView ? 'running' : 'paused' }} />
                  </span>
                )}
                <span className="lp-trap-n">0{i + 1}</span>
                <span className="lp-trap-text">
                  <b>{t.name}</b>
                  <span className="lp-trap-hook">{t.hook}</span>
                  {i === active && <span className="lp-trap-body">{t.body}</span>}
                </span>
              </button>
            </li>
          ))}
        </ol>

        <div className="lp-trap-view">
          <div className="lp-trap-card">
            <div className={`lp-vstrip ${phase === 1 ? 'on' : ''}`} aria-live="polite">
              <span className="lp-vstrip-label">{BRAND.name} says</span>
              {phase === 1 ? (
                <span className="lp-vstrip-body" key={trap.id}>
                  <Badge level={trap.verdict.level} />
                  <span className="lp-vstrip-text">
                    <b>{trap.verdict.title}.</b> {trap.verdict.detail}
                  </span>
                </span>
              ) : (
                <span className="lp-vstrip-body">
                  <span className="lp-badge checking">Checking</span>
                  <span className="lp-vstrip-text">Reading the wallets…</span>
                </span>
              )}
            </div>
            <div className="lp-stage lp-stage-trap">
              <SceneCanvas key={trap.id} scene={trap.scene} playing={inView} onPhase={setPhase} label={`${trap.name}, shown on a bubble map`} />
            </div>
          </div>
          <p className="lp-trap-body-m">{trap.body}</p>
        </div>
      </div>
    </section>
  );
}

/* ================================================================ answer */

const LEVELS: { level: RiskLevel; head: string; reasons: { level: RiskLevel; b: string; t: string }[]; stats: [string, string, string]; note?: string }[] = [
  {
    level: 'low',
    head: 'No linked groups among the top holders',
    reasons: [],
    note: 'The 10 biggest wallets hold 18% and none of them share a funder or a launch block. Low risk is not no risk: still check liquidity.',
    stats: ['18.2%', '0%', '2,904'],
  },
  {
    level: 'medium',
    head: 'One linked group holds 9% of the supply',
    reasons: [
      { level: 'medium', b: 'Same source of money.', t: '6 wallets were funded by one wallet before they bought.' },
      { level: 'medium', b: 'A few wallets hold a lot.', t: 'The top 10 hold 34%.' },
    ],
    stats: ['34.0%', '9.1%', '1,317'],
  },
  {
    level: 'high',
    head: 'Linked wallets hold 41% of the supply',
    reasons: [
      { level: 'high', b: 'The launch was bundled.', t: '8 wallets bought in the launch block.' },
      { level: 'high', b: 'A linked group is selling.', t: '5 wallets sold 6% in the last hour.' },
      { level: 'medium', b: 'The creator still holds a lot.', t: '11% across 3 wallets.' },
    ],
    stats: ['52.7%', '41.0%', '1,480'],
  },
];

function Answer() {
  const [i, setI] = useState(2);
  const [auto, setAuto] = useState(true);
  const [ref, inView] = useInView<HTMLDivElement>();
  const [headRef, headIn] = useInView<HTMLDivElement>({ once: true });
  useEffect(() => {
    if (!auto || !inView) return;
    const id = setTimeout(() => setI((x) => (x + 1) % 3), 3600);
    return () => clearTimeout(id);
  }, [i, auto, inView]);
  const L = LEVELS[i]!;

  return (
    <section className="lp-section lp-answer" id="answer">
      <div ref={headRef} className={`lp-answer-copy lp-reveal ${headIn ? 'in' : ''}`}>
        <span className="lp-kicker">The verdict</span>
        <h2>One word first. Then the receipts.</h2>
        <p>You don’t have to read a single wallet. {BRAND.name} reads all of them and tells you who controls the supply before you trade.</p>
        <ol className="lp-answer-points">
          <li>
            <span>1</span>
            <div>
              <b>A risk level.</b> Low, Medium or High, the moment the map is ready.
            </div>
          </li>
          <li>
            <span>2</span>
            <div>
              <b>The reasons, in plain English.</b> “The launch was bundled”, not “cluster 3 entropy 0.82”.
            </div>
          </li>
          <li>
            <span>3</span>
            <div>
              <b>The proof, one click away.</b> Every claim opens the exact wallets, trades and money trail behind it.
            </div>
          </li>
        </ol>
      </div>

      <div className="lp-answer-demo" ref={ref}>
        <div className="lp-meter" role="tablist" aria-label="Risk levels">
          {LEVELS.map((l, k) => (
            <button
              key={l.level}
              role="tab"
              aria-selected={k === i}
              className={`lp-meter-seg ${l.level} ${k === i ? 'on' : ''}`}
              onClick={() => {
                setAuto(false);
                setI(k);
              }}
            >
              <i />
              <span>{l.level === 'low' ? 'Low' : l.level === 'medium' ? 'Medium' : 'High'}</span>
            </button>
          ))}
        </div>
        <div className="lp-vcard lp-vcard-lg" key={L.level}>
          <div className="lp-vcard-top">
            <Badge level={L.level} />
            <span className="lp-vcard-tok">
              <i className={`lp-tok ${L.level}`} /> Example token
            </span>
          </div>
          <b className="lp-vcard-head">{L.head}</b>
          {L.note && <p className="lp-vcard-note">{L.note}</p>}
          {L.reasons.length > 0 && (
            <ul className="lp-reasons">
              {L.reasons.map((r) => (
                <li key={r.b} className={r.level}>
                  <b>{r.b}</b> {r.t}
                </li>
              ))}
            </ul>
          )}
          <div className="lp-vstats">
            <div>
              <em>Top 10 hold</em>
              <b>{L.stats[0]}</b>
            </div>
            <div>
              <em>Linked hold</em>
              <b className={L.level}>{L.stats[1]}</b>
            </div>
            <div>
              <em>Holders</em>
              <b>{L.stats[2]}</b>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* =============================================================== map key */

function MapKey() {
  const [ref, inView] = useInView<HTMLDivElement>({ once: true });
  return (
    <section className="lp-section lp-keysec">
      <div ref={ref} className={`lp-reveal ${inView ? 'in' : ''}`}>
        <span className="lp-kicker">Reading the map</span>
        <h2>Three rules. Five seconds.</h2>
      </div>
      <ol className={`lp-key ${inView ? 'in' : ''}`}>
        <li>
          <svg viewBox="0 0 140 72" aria-hidden="true" className="lp-glyph g-size">
            <circle cx="40" cy="38" r="24" className="g-solo g-grow" />
            <circle cx="88" cy="44" r="12" className="g-solo g-dim" />
            <circle cx="116" cy="26" r="7" className="g-solo g-dim" />
          </svg>
          <h3>Bigger bubble, bigger holder</h3>
          <p>Every bubble is a wallet. Its size is the share of the supply it holds right now.</p>
        </li>
        <li>
          <svg viewBox="0 0 140 72" aria-hidden="true" className="lp-glyph g-group">
            <circle cx="30" cy="26" r="10" className="g-pink g-pop1" />
            <circle cx="56" cy="46" r="13" className="g-pink g-pop2" />
            <circle cx="80" cy="22" r="8" className="g-pink g-pop3" />
            <circle cx="116" cy="44" r="11" className="g-solo" />
          </svg>
          <h3>Same colour, same people</h3>
          <p>Wallets funded from one source or that bought together at launch share a colour. Grey ones look independent.</p>
        </li>
        <li>
          <svg viewBox="0 0 140 72" aria-hidden="true" className="lp-glyph g-flow">
            <path d="M30 40 L70 24 L112 44" className="g-line" />
            <circle cx="30" cy="40" r="11" className="g-solo" />
            <circle cx="70" cy="24" r="7" className="g-ring" />
            <circle cx="112" cy="44" r="13" className="g-solo" />
          </svg>
          <h3>Lines are money moving</h3>
          <p>A line means SOL or tokens moved between two wallets. A dashed ring is a funding wallet that holds none of the token.</p>
        </li>
      </ol>
    </section>
  );
}

/* =================================================================== faq */

const FAQ: { q: string; a: string }[] = [
  { q: 'Which tokens can I check?', a: 'Any Solana SPL token: Pump.fun, Raydium, Meteora and the rest. Paste the token address or search by name.' },
  { q: 'Do I need to connect a wallet?', a: `No. ${BRAND.name} only reads public on-chain data. It never asks for a wallet connection, a signature or a private key.` },
  { q: 'Where does the data come from?', a: 'Straight from the Solana chain via Helius, with market data from DexScreener. Every claim in a verdict links to the wallets and transactions behind it.' },
  { q: 'What does “linked” mean?', a: 'Two wallets are linked when the same wallet funded both before they bought, or when they bought in the same launch block. Linked wallets usually belong to the same people.' },
  { q: 'Does Low risk mean the token is safe?', a: 'No. Low risk means no linked groups among the top holders. It says nothing about liquidity, the team or the price. Always check those too.' },
  { q: 'Is this financial advice?', a: `No. ${BRAND.name} shows who holds a token and how those wallets are connected. What you do with that is your call.` },
];

function Faq() {
  const [ref, inView] = useInView<HTMLDivElement>({ once: true });
  const [open, setOpen] = useState(0);
  return (
    <section className="lp-section lp-faq" id="faq">
      <div ref={ref} className={`lp-reveal ${inView ? 'in' : ''}`}>
        <span className="lp-kicker">Questions</span>
        <h2>Good to know.</h2>
        <p className="lp-faq-side">The short version: paste a token, read one risk word, open the receipts if you want the proof. No wallet, no setup.</p>
      </div>
      <div className="lp-faq-list">
        {FAQ.map((f, i) => (
          <div key={f.q} className={`lp-faq-item ${open === i ? 'on' : ''}`}>
            <button aria-expanded={open === i} onClick={() => setOpen(open === i ? -1 : i)}>
              <span>{f.q}</span>
              <i aria-hidden="true" />
            </button>
            <div className="lp-faq-a" role="region">
              <p>{f.a}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ============================================================= final cta */

function FinalCta({ signedOut }: { signedOut: boolean }) {
  return (
    <section className="lp-cta">
      <div className="lp-dots" aria-hidden="true" />
      <div className="lp-cta-inner">
        <h2>
          Before your next buy, <em>map the trap.</em>
        </h2>
        <div className="lp-cta-box">
          <SearchCommand variant="hero" locked={signedOut} />
          <span className="lp-cta-note">Paste a Solana token address. Pump.fun, Raydium, Meteora and any other SPL token.</span>
          <div className="lp-cta-social">
            <span>Updates and new trap patterns, first:</span>
            <SocialLinks big />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ================================================================ shared */

function Badge({ level }: { level: RiskLevel }) {
  return <span className={`lp-badge ${level}`}>{level === 'low' ? 'Low' : level === 'medium' ? 'Medium' : 'High'} risk</span>;
}

function Dots() {
  return (
    <span className="lp-dots-anim" aria-label="working">
      <i />
      <i />
      <i />
    </span>
  );
}

function SocialLinks({ big = false }: { big?: boolean }) {
  return (
    <span className={`lp-social ${big ? 'big' : ''}`}>
      <a href={BRAND.x} target="_blank" rel="noreferrer" aria-label={`${BRAND.name} on X`}>
        <IconX />
        {big && <span>Follow on X</span>}
      </a>
      <a href={BRAND.telegram} target="_blank" rel="noreferrer" aria-label={`${BRAND.name} on Telegram`}>
        <IconTelegram />
        {big && <span>Join the Telegram</span>}
      </a>
    </span>
  );
}

export function IconX({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d="M17.75 3h3.07l-6.7 7.66L22 21h-6.17l-4.83-6.32L5.47 21H2.4l7.17-8.2L2 3h6.33l4.37 5.78L17.75 3Zm-1.08 16.2h1.7L7.4 4.7H5.58l11.09 14.5Z" />
    </svg>
  );
}

export function IconTelegram({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d="M21.94 4.3 18.9 19.01c-.23 1.03-.83 1.29-1.69.8l-4.66-3.44-2.25 2.17c-.25.25-.46.46-.94.46l.34-4.76 8.66-7.83c.38-.34-.08-.53-.58-.19L7.08 12.97l-4.6-1.44c-1-.31-1.02-1 .21-1.48l18-6.94c.83-.31 1.56.19 1.25 1.19Z" />
    </svg>
  );
}

